import React, { FormEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Alert, Box, Button, Container, Stack, Tab, Tabs, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useListAchievements } from '~/hooks/apis/agent/useListAchievements';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { formatDateSlashes } from '~/services/TimeService';

import { AchievementListRow, GetAchievementsParams } from '~/types/services/agentHub';

import { capitalise } from '~/utils/stringUtils';

import AchievementDialog from './AchievementDialog';
import AchievementSubjectName from './AchievementSubjectName';

const DEFAULT_SIZE_PER_PAGE = 10;
const STATUS_TABS = ['', 'active', 'inactive'] as const;

type AchievementStatusFilter = (typeof STATUS_TABS)[number];

type FilterState = {
  userId: string;
  rewardId: string;
};

function getOptionalFilter(value: string) {
  const trimmedValue = value.trim();
  return trimmedValue === '' ? undefined : trimmedValue;
}

function getPageFromQuery(page: string | null) {
  const pageNumber = parseInt(page ?? '0', 10);
  return Number.isFinite(pageNumber) && pageNumber >= 0 ? pageNumber : 0;
}

function getStatusFromQuery(status: string | null): AchievementStatusFilter {
  if (status === 'active' || status === 'inactive') {
    return status;
  }
  return '';
}

export default function AgentAchievements() {
  const queryFilters = useQuery();
  const { push: setQueryString } = useAppNavigation();
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const filters = useMemo(
    () => ({
      page: getPageFromQuery(queryFilters.get('page')),
      status: getStatusFromQuery(queryFilters.get('status')),
      userId: queryFilters.get('userId') ?? '',
      rewardId: queryFilters.get('rewardId') ?? '',
    }),
    [queryFilters],
  );

  const [filterInputs, setFilterInputs] = useState<FilterState>({
    userId: filters.userId,
    rewardId: filters.rewardId,
  });

  useEffect(() => {
    setFilterInputs({
      userId: filters.userId,
      rewardId: filters.rewardId,
    });
  }, [filters.rewardId, filters.userId]);

  const queryParams = useMemo<GetAchievementsParams>(
    () => ({
      page: filters.page.toString(),
      pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.userId ? { userId: filters.userId } : {}),
      ...(filters.rewardId ? { rewardId: filters.rewardId } : {}),
    }),
    [filters.page, filters.rewardId, filters.status, filters.userId],
  );

  const { data: { rows = [], total = 0 } = {}, isFetching, error } = useListAchievements(queryParams);

  const updateQueryString = useCallback(
    (nextFilters: FilterState, page = 0, status = filters.status) => {
      const nextQueryFilters = new URLSearchParams();
      nextQueryFilters.set('page', page.toString());
      if (status) {
        nextQueryFilters.set('status', status);
      }

      const userId = getOptionalFilter(nextFilters.userId);
      const rewardId = getOptionalFilter(nextFilters.rewardId);
      if (userId) {
        nextQueryFilters.set('userId', userId);
      }
      if (rewardId) {
        nextQueryFilters.set('rewardId', rewardId);
      }

      setQueryString({ search: nextQueryFilters.toString() });
    },
    [filters.status, setQueryString],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      updateQueryString(filterInputs);
    },
    [filterInputs, updateQueryString],
  );

  const handleClear = useCallback(() => {
    const emptyFilters = { userId: '', rewardId: '' };
    setFilterInputs(emptyFilters);
    updateQueryString(emptyFilters, 0, '');
  }, [updateQueryString]);

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, nextTab: number) => {
      updateQueryString(filterInputs, 0, STATUS_TABS[nextTab]);
    },
    [filterInputs, updateQueryString],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateQueryString(filterInputs, page);
    },
    [filterInputs, updateQueryString],
  );

  const columns: Array<GridColDef<AchievementListRow>> = useMemo(
    () => [
      { field: 'rewardName', headerName: 'Reward', flex: 1.5, minWidth: 180 },
      {
        field: 'subjectType',
        headerName: 'Subject',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => capitalise(String(value)),
      },
      {
        field: 'subjectName',
        headerName: 'Name',
        flex: 1.5,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => <AchievementSubjectName subjectType={row.subjectType} subjectId={row.subjectId} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 110,
        valueFormatter: (value) => capitalise(String(value)),
      },
      {
        field: 'earnedAt',
        headerName: 'Earned',
        flex: 1,
        minWidth: 140,
        valueFormatter: (value) => formatDateSlashes(typeof value === 'string' ? value : undefined),
      },
    ],
    [],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Agent Achievements</title>
      </Helmet>
      <Stack pt={2} direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <PageSubheader title="Achievements" />
      </Stack>
      <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="User ID"
            value={filterInputs.userId}
            onChange={(event) => setFilterInputs((current) => ({ ...current, userId: event.target.value }))}
            size="small"
          />
          <TextField
            label="Reward ID"
            value={filterInputs.rewardId}
            onChange={(event) => setFilterInputs((current) => ({ ...current, rewardId: event.target.value }))}
            size="small"
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
          <Button type="button" variant="text" onClick={handleClear}>
            Clear filters
          </Button>
        </Stack>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs value={STATUS_TABS.indexOf(filters.status)} onChange={handleTabChange} aria-label="achievement status">
          <Tab label="All" />
          <Tab label="Active" />
          <Tab label="Inactive" />
        </Tabs>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Unable to load achievements.
        </Alert>
      )}
      <Box sx={{ pt: 3 }}>
        <DataGrid
          loading={isFetching}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          autoHeight
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page: filters.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => handlePageChange(page)}
          onRowClick={(params) => setSelectedId(params.row.id)}
        />
      </Box>
      <AchievementDialog achievementId={selectedId} open={!!selectedId} onClose={() => setSelectedId(undefined)} />
    </Container>
  );
}
